import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { useAuthSession } from '@/features/auth/useAuthSession';
import { track } from '@/services/analytics';
import {
  completeRewardedAd,
  fetchRewardedAdAvailability,
  startRewardedAd,
  type RewardedAdCompletion,
  type RewardedAdStart,
} from '@/services/rewardedAds';

import { runRewardedAd, type RewardedAdPlayback } from './run-rewarded-ad';

/**
 * Состояние одного показа рекламы.
 *
 * Хук ничего не решает про награду: доступность, размер и факт начисления
 * приходят с сервера. Его работа — довести показ от кнопки до ответа
 * сервера и не обратиться за наградой, если показ прервали.
 */
export type RewardedAdPhase = 'idle' | 'starting' | 'playing' | 'finishing';

export function useRewardedAd() {
  const { status, session } = useAuthSession();
  const queryClient = useQueryClient();

  const availability = useQuery({
    queryKey: ['rewarded-ad', session?.user.id ?? null],
    queryFn: () => fetchRewardedAdAvailability(session?.access_token ?? ''),
    enabled: status === 'ready' && Boolean(session),
  });

  const [phase, setPhase] = useState<RewardedAdPhase>('idle');
  const [playing, setPlaying] = useState<RewardedAdStart | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completion, setCompletion] = useState<RewardedAdCompletion | null>(null);

  // Показ заканчивает человек, а не код: промис показа держится здесь и
  // разрешается кнопкой «Получить награду» или «Закрыть».
  const finishPlayback = useRef<((playback: RewardedAdPlayback) => void) | null>(null);

  const watch = useCallback(() => {
    const token = session?.access_token;

    if (!token || phase !== 'idle') {
      return;
    }

    setError(null);
    setCompletion(null);
    setPhase('starting');

    void runRewardedAd({
      start: async () => {
        const started = await startRewardedAd(token);
        track('ad_started');
        return started;
      },
      play: (started) =>
        new Promise<RewardedAdPlayback>((resolve) => {
          finishPlayback.current = resolve;
          setPlaying(started);
          setPhase('playing');
        }),
      complete: (input) => {
        // Досмотрел до конца: награды может и не быть (лимит, отказ
        // провайдера), поэтому событие о показе отдельно от начисления —
        // ad_rewarded пишет сервер.
        track('ad_completed');
        setPhase('finishing');
        return completeRewardedAd(input, token);
      },
    })
      .then((run) => {
        if (run.outcome === 'rewarded') {
          setCompletion(run.completion);
          // Баланс изменил сервер — перечитываем его, а не считаем сами.
          void queryClient.invalidateQueries({ queryKey: ['credits'] });
        }

        void queryClient.invalidateQueries({ queryKey: ['rewarded-ad'] });
      })
      .catch((caught: unknown) => {
        setError(
          caught instanceof Error
            ? caught.message
            : 'Не удалось показать рекламу. Попробуйте позже.',
        );
      })
      .finally(() => {
        finishPlayback.current = null;
        setPlaying(null);
        setPhase('idle');
      });
  }, [phase, queryClient, session]);

  const finish = useCallback(() => {
    finishPlayback.current?.({ completed: true, providerEventId: null });
  }, []);

  const cancel = useCallback(() => {
    finishPlayback.current?.({ completed: false, providerEventId: null });
  }, []);

  return { availability, phase, playing, error, completion, watch, finish, cancel };
}
